import { z } from 'zod';

/**
 * Centralized password validation schema and helpers.
 * 
 * Strong password requirements:
 * - Minimum 8 characters
 * - At least 1 uppercase letter (A-Z)
 * - At least 1 lowercase letter (a-z)
 * - At least 1 number (0-9)
 * - At least 1 special character (!@#$%^&*(),.?":{}|<>)
 */

// Strong password schema for signup and password reset
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character');

// Login schema - relaxed for existing users with weak passwords
export const loginPasswordSchema = z
  .string()
  .min(1, 'Password is required');

// Validation result for real-time strength checking
export interface PasswordStrengthChecks {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

// Helper function for real-time strength checking
export const validatePasswordStrength = (password: string): PasswordStrengthChecks => {
  return {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
};

// Get overall strength score (0-5)
export const getPasswordStrength = (password: string): number => {
  const checks = validatePasswordStrength(password);
  return Object.values(checks).filter(Boolean).length;
};

// Check if password meets all requirements
export const isPasswordStrong = (password: string): boolean => {
  return getPasswordStrength(password) === 5;
};

// Email validation schema
export const emailSchema = z.string().email('Please enter a valid email');

// Signup validation schema
export const signupSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  referralCode: z.string().optional(),
});
